import { FormEvent, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

/**
 * IntakeForm — six-question "send the shape" pre-fit.
 *
 * Aligns with QualificationAnswersV1 from the Public Revenue Spine copy
 * finalization pack. Posts to /api/lead-inquiry with the existing schema
 * by packing the six answers into qualification_answers as a structured
 * record. Boundary copy: "Send the shape, not the private data."
 *
 * Public-safe by design: no private architecture, no internal methods,
 * no vendor names, no pricing values.
 */

const QUALIFICATION_VERSION_V1 = 'v1.0-2026-05-17';

type HelpShape = 'map' | 'build' | 'advisory' | 'unsure';
type BoundaryPreference = 'async-first' | 'next-step';

type IntakeFormState = {
  name: string;
  email: string;
  q1_shape: string;
  q2_friction: string;
  q3_fix: string;
  q4_history: string;
  q5_help: HelpShape | '';
  q6_boundary: BoundaryPreference | '';
  sensitiveAck: boolean;
};

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

type SubmitError = {
  status: number | null;
  message: string;
};

type LeadInquiryResponse = {
  ok?: boolean;
  lead_id?: string;
  deduped?: boolean;
  stage2_appended?: boolean;
  warning?: string;
  error?: string;
  continue_token?: string;
  continue_url?: string;
};

type SenderContext = {
  timezone: string | null;
  timezone_offset_minutes: number | null;
  timezone_source: 'browser_intl' | 'manual_override' | 'unknown';
  locale: string | null;
  submitted_at_local: string | null;
  submitted_at_utc: string;
  capture_version: 'tz-capture-v0.1';
};

type IntakeFormProps = {
  sourceRoute?: string;
  sourceHash?: string;
  extraTags?: string[];
  prefill?: Partial<Pick<IntakeFormState, 'name' | 'email'>>;
  stage2Prefill?: Partial<
    Pick<
      IntakeFormState,
      'q1_shape' | 'q2_friction' | 'q3_fix' | 'q4_history' | 'q5_help' | 'q6_boundary'
    >
  >;
  contactLocked?: boolean;
  stage2UpdateToken?: string;
};

const initialState: IntakeFormState = {
  name: '',
  email: '',
  q1_shape: '',
  q2_friction: '',
  q3_fix: '',
  q4_history: '',
  q5_help: '',
  q6_boundary: '',
  sensitiveAck: false,
};

const helpOptions: Array<{ value: HelpShape; label: string }> = [
  { value: 'map', label: "A clear map of how things actually work today, with the next moves" },
  { value: 'build', label: "A short build sprint — implement a specific change with my own hands" },
  { value: 'advisory', label: 'Advisory — someone to think with, not someone to do the work' },
  { value: 'unsure', label: "I'm not sure yet — help me figure that out" },
];

const boundaryOptions: Array<{ value: BoundaryPreference; label: string }> = [
  { value: 'async-first', label: "Yes, let's do async prefit first" },
  { value: 'next-step', label: 'No, send me the next step directly' },
];

const sensitiveTerms = [
  'api key',
  'bank statement',
  'client list',
  'confidential',
  'credential',
  'customer list',
  'password',
  'payroll',
  'private file',
  'screenshot',
  'tax return',
  'vendor list',
];

function hasUsableEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function detectSensitiveRisk(haystack: string) {
  const normalized = haystack.toLowerCase();
  return sensitiveTerms.some((term) => normalized.includes(term));
}

function HelpText({ children }: { children: string }) {
  return <p className="text-xs leading-relaxed text-ink/55">{children}</p>;
}

function ChoiceButton({
  selected,
  onClick,
  label,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-sm border p-3 text-left text-sm leading-snug transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'border-ink bg-ink text-paper shadow-[0_12px_30px_rgba(11,13,16,0.10)]'
          : 'border-ink/15 bg-white text-ink hover:border-ink/40 hover:bg-ink/[0.02]',
      )}
    >
      {label}
    </button>
  );
}

function buildMailtoFallback(form: IntakeFormState): string {
  const lines = [
    'Hi JL,',
    '',
    'My submission via the intake form did not go through, so I am sending the answers directly.',
    '',
    `Name: ${form.name.trim() || '(blank)'}`,
    `Email: ${form.email.trim() || '(blank)'}`,
    '',
    '1. Shape of the business:',
    form.q1_shape.trim() || '(blank)',
    '',
    '2. System or decision slowing me down:',
    form.q2_friction.trim() || '(blank)',
    '',
    '3. What "fixed" looks like in 90 days:',
    form.q3_fix.trim() || '(blank)',
    '',
    '4. Past attempts:',
    form.q4_history.trim() || '(skipped)',
    '',
    `5. Help shape: ${form.q5_help || '(blank)'}`,
    `6. Async preference: ${form.q6_boundary || '(blank)'}`,
    '',
    'I confirm this message contains no confidential or regulated data.',
  ];
  const subject = 'Intake — Send the shape';
  return `mailto:jl@jlpatawaran.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join('\n'))}`;
}

function captureSenderContext(now: Date = new Date()): SenderContext {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    const timezone = resolved.timeZone || null;
    const offsetMinutes = -now.getTimezoneOffset();
    return {
      timezone,
      timezone_offset_minutes: Number.isFinite(offsetMinutes) ? offsetMinutes : null,
      timezone_source: timezone ? 'browser_intl' : 'unknown',
      locale: typeof navigator !== 'undefined' ? navigator.language || null : null,
      submitted_at_local: formatLocalIsoWithOffset(now),
      submitted_at_utc: now.toISOString(),
      capture_version: 'tz-capture-v0.1',
    };
  } catch {
    return {
      timezone: null,
      timezone_offset_minutes: null,
      timezone_source: 'unknown',
      locale: null,
      submitted_at_local: null,
      submitted_at_utc: now.toISOString(),
      capture_version: 'tz-capture-v0.1',
    };
  }
}

function formatLocalIsoWithOffset(date: Date): string {
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffset / 60)).padStart(2, '0');
  const offsetRemainder = String(absoluteOffset % 60).padStart(2, '0');
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${sign}${offsetHours}:${offsetRemainder}`;
}

export function IntakeForm({
  sourceRoute = '/work-with-me',
  sourceHash = '',
  extraTags = [],
  prefill,
  stage2Prefill,
  contactLocked = false,
  stage2UpdateToken,
}: IntakeFormProps) {
  const [form, setForm] = useState<IntakeFormState>(() => ({
    ...initialState,
    name: prefill?.name ?? '',
    email: prefill?.email ?? '',
    q1_shape: stage2Prefill?.q1_shape ?? '',
    q2_friction: stage2Prefill?.q2_friction ?? '',
    q3_fix: stage2Prefill?.q3_fix ?? '',
    q4_history: stage2Prefill?.q4_history ?? '',
    q5_help: stage2Prefill?.q5_help ?? '',
    q6_boundary: stage2Prefill?.q6_boundary ?? '',
  }));
  const [attempted, setAttempted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const sensitiveWarning = useMemo(
    () =>
      detectSensitiveRisk(
        [form.q1_shape, form.q2_friction, form.q3_fix, form.q4_history].join(' '),
      ),
    [form.q1_shape, form.q2_friction, form.q3_fix, form.q4_history],
  );

  const requiredReady =
    form.name.trim().length > 0 &&
    hasUsableEmail(form.email) &&
    form.q1_shape.trim().length > 0 &&
    form.q2_friction.trim().length > 0 &&
    form.q3_fix.trim().length > 0 &&
    Boolean(form.q5_help) &&
    Boolean(form.q6_boundary) &&
    form.sensitiveAck;

  function update<K extends keyof IntakeFormState>(key: K, value: IntakeFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
      setSubmitError(null);
      setSuccessMessage(null);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAttempted(true);
    if (!requiredReady) return;

    setSubmitStatus('submitting');

    const sourceUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}${window.location.pathname}${sourceHash}`
        : 'https://jlpatawaran.com/work-with-me';

    const submittedAt = new Date();
    const qualificationAnswersV1 = {
      q1_shape: form.q1_shape.trim(),
      q2_friction: form.q2_friction.trim(),
      q3_fix: form.q3_fix.trim(),
      q4_history: form.q4_history.trim() || undefined,
      q5_help: form.q5_help,
      q6_boundary: form.q6_boundary,
      sender_context: captureSenderContext(submittedAt),
    };

    const intentShape = (() => {
      switch (form.q5_help) {
        case 'map':
          return 'Map';
        case 'build':
          return 'Build Sprint';
        case 'advisory':
          return 'Advisory';
        default:
          return 'Send the shape';
      }
    })();

    const tags = [
      'source:jlpatawaran.com',
      `qualification:${QUALIFICATION_VERSION_V1}`,
      `q5_help:${form.q5_help}`,
      `q6_boundary:${form.q6_boundary}`,
      ...extraTags,
    ];
    if (sensitiveWarning) tags.push('risk:sensitive-language');

    const recommendedRoute =
      form.q5_help === 'build'
        ? 'build-sprint-prefit'
        : form.q5_help === 'advisory'
          ? 'advisory-prefit'
          : form.q5_help === 'unsure'
            ? 'manual-prefit-review'
            : 'map-prefit';

    try {
      const response = await fetch('/api/lead-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          intent_shape: intentShape,
          urgency:
            form.q6_boundary === 'async-first' ? 'Exploring fit (async first)' : 'Ready for next step',
          message: '', // intake answers carry the signal; no free-text required
          sensitive_data_ack: form.sensitiveAck,
          qualification_version: QUALIFICATION_VERSION_V1,
          qualification_answers: qualificationAnswersV1,
          qualification_score: 0,
          qualification_temperature: 'unscored' as const,
          qualification_tags: tags,
          recommended_route: recommendedRoute,
          recommended_next_action:
            'Manual JL review. Reply within two working days with a clear next step.',
          missing_info: [],
          sensitive_risk_flag: sensitiveWarning,
          survey_completed_at: submittedAt.toISOString(),
          source_route: sourceRoute,
          source_url: sourceUrl,
          ...(stage2UpdateToken ? { stage2_update_token: stage2UpdateToken } : {}),
        }),
      });

      const body = (await response.json().catch(() => ({}))) as LeadInquiryResponse;

      if (!response.ok) {
        let errorMessage = 'Submission did not reach the intake endpoint.';
        if (body && typeof body.error === 'string') errorMessage = body.error;
        setSubmitError({ status: response.status, message: errorMessage });
        setSubmitStatus('error');
        return;
      }
      setSuccessMessage(
        body.deduped
          ? 'Already received. This same Stage 2 priority note is already attached to the intake queue, so I did not create a second copy.'
          : body.warning ||
              "I read every answer myself, usually within one working day. You'll hear back within two working days with a next step.",
      );
      setSubmitStatus('success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error';
      setSubmitError({ status: null, message });
      setSubmitStatus('error');
    }
  }

  if (submitStatus === 'success') {
    return (
      <div className="rounded-sm border border-ink/10 bg-white p-8 shadow-[0_18px_60px_rgba(11,13,16,0.06)]">
        <p className="font-display text-xs uppercase tracking-[0.22em] text-accent">
          {successMessage?.startsWith('Already received') ? 'Already received' : 'Sent'}
        </p>
        <h3 className="mt-4 font-display text-2xl tracking-tight">
          {successMessage?.startsWith('Already received')
            ? 'No duplicate copy created.'
            : 'Got it. Thank you for the shape.'}
        </h3>
        <div className="mt-5 grid gap-3 text-ink/75">
          <p>{successMessage}</p>
          <ul className="mt-2 grid gap-2 border-l-2 border-accent pl-4 text-sm text-ink/70">
            <li>If the next step is async prefit, I'll send a one-page document to your email.</li>
            <li>If the next step is a direct booking, I'll send a single calendar link.</li>
            <li>If we're not a fit, I'll send a short note explaining why, and a referral if I can.</li>
          </ul>
          <p className="mt-4 text-sm text-ink/55">
            In the meantime, no follow-up needed from your side.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-sm border border-ink/10 bg-white shadow-[0_18px_60px_rgba(11,13,16,0.06)]"
    >
      <div className="border-b border-ink/10 p-6 sm:p-8">
        <p className="font-display text-xs uppercase tracking-[0.22em] text-accent">
          Send the shape, not the private data
        </p>
        <h3 className="mt-3 font-display text-2xl tracking-tight">
          Six short questions. About five minutes.
        </h3>
        <p className="mt-3 text-sm text-ink/70">
          No files, no screenshots, no exports, no credentials, no client or vendor
          names, no monetary values.
        </p>
        <p className="mt-2 text-sm text-ink/70">
          I read every answer myself. If we're a fit, you'll hear from me within
          two working days with a next step — usually an async prefit, sometimes a
          direct booking, sometimes a referral to someone better suited. If we're
          not a fit, I'll say so plainly.
        </p>
      </div>

      <div className="grid gap-7 p-6 sm:p-8">
        {contactLocked ? (
          <div className="rounded-sm border border-ink/10 bg-paper p-4 text-sm text-ink/65">
            Using async prefit contact:{' '}
            <span className="font-semibold text-ink">{form.name}</span> /{' '}
            <span className="font-semibold text-ink">{form.email}</span>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="intake-name">Name</Label>
              <Input
                id="intake-name"
                required
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
              />
              <HelpText>A real name helps route the reply cleanly.</HelpText>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="intake-email">Email</Label>
              <Input
                id="intake-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
              />
              <HelpText>Used only for the manual reply.</HelpText>
            </div>
          </div>
        )}

        {/* Q1 — Shape */}
        <div className="grid gap-2">
          <Label htmlFor="q1_shape">
            1. What's the shape of your business right now?
          </Label>
          <HelpText>Just enough so I can route correctly. Stage, team size, entity count if that matters.</HelpText>
          <Input
            id="q1_shape"
            required
            maxLength={280}
            placeholder="Two-year-old services business, three of us, one entity"
            value={form.q1_shape}
            onChange={(e) => update('q1_shape', e.target.value)}
          />
        </div>

        {/* Q2 — Friction */}
        <div className="grid gap-2">
          <Label htmlFor="q2_friction">
            2. What system or decision is currently slowing you down most?
          </Label>
          <HelpText>One thing, not everything. Be specific in shape, not in detail.</HelpText>
          <Textarea
            id="q2_friction"
            required
            maxLength={500}
            placeholder="Month-end close keeps slipping; the books and the spreadsheets don't agree."
            value={form.q2_friction}
            onChange={(e) => update('q2_friction', e.target.value)}
            className="min-h-24"
          />
        </div>

        {/* Q3 — Fix */}
        <div className="grid gap-2">
          <Label htmlFor="q3_fix">
            3. What does "fixed" look like for you in the next 90 days?
          </Label>
          <HelpText>A concrete outcome you'd be able to see. Time, money, hours, clarity — your choice of metric, but specific.</HelpText>
          <Textarea
            id="q3_fix"
            required
            maxLength={500}
            placeholder="Close lands by working-day five every month, and I trust the cash number without recompiling it."
            value={form.q3_fix}
            onChange={(e) => update('q3_fix', e.target.value)}
            className="min-h-24"
          />
        </div>

        {/* Q4 — History */}
        <div className="grid gap-2">
          <Label htmlFor="q4_history">
            4. Have you tried to solve this before? What stopped working? (optional)
          </Label>
          <HelpText>Optional but useful. Tells me what you've already ruled out.</HelpText>
          <Textarea
            id="q4_history"
            maxLength={500}
            placeholder="Hired a part-time bookkeeper last year; the books got cleaner but the cash view stayed broken."
            value={form.q4_history}
            onChange={(e) => update('q4_history', e.target.value)}
            className="min-h-20"
          />
        </div>

        {/* Q5 — Help shape */}
        <fieldset className="grid gap-3">
          <legend className="text-sm font-medium text-ink">
            5. What kind of help fits best?
          </legend>
          <HelpText>If you're unsure, pick the closest. We can adjust on the first call.</HelpText>
          <div className="grid gap-3 md:grid-cols-2">
            {helpOptions.map((opt) => (
              <ChoiceButton
                key={opt.value}
                selected={form.q5_help === opt.value}
                onClick={() => update('q5_help', opt.value)}
                label={opt.label}
              />
            ))}
          </div>
        </fieldset>

        {/* Q6 — Boundary preference */}
        <fieldset className="grid gap-3">
          <legend className="text-sm font-medium text-ink">
            6. Anything you'd rather discuss async first?
          </legend>
          <HelpText>If you have context you don't want in a form, that's fine. Pick async and I'll send a one-page prefit to fill in privately.</HelpText>
          <div className="grid gap-3 md:grid-cols-2">
            {boundaryOptions.map((opt) => (
              <ChoiceButton
                key={opt.value}
                selected={form.q6_boundary === opt.value}
                onClick={() => update('q6_boundary', opt.value)}
                label={opt.label}
              />
            ))}
          </div>
        </fieldset>

        {sensitiveWarning && (
          <p className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Sensitive-language check triggered. Keep the answers general and remove
            private records, names, values, screenshots, or credentials before sending.
          </p>
        )}

        <label className="flex gap-3 rounded-sm border border-ink/10 bg-paper p-4 text-sm leading-relaxed text-ink/75">
          <input
            type="checkbox"
            checked={form.sensitiveAck}
            onChange={(e) => update('sensitiveAck', e.target.checked)}
            className="mt-1 h-4 w-4 shrink-0 accent-ink"
          />
          <span>
            By submitting, I confirm this message contains no confidential or regulated
            data. I understand JL does not store form responses on third-party marketing
            platforms.
          </span>
        </label>

        {attempted && !requiredReady && (
          <p className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Complete the required fields and confirm the boundary acknowledgement before sending.
          </p>
        )}

        {submitStatus === 'error' && (
          <div className="rounded-sm border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            <p className="font-semibold">Your form couldn't reach my inbox right now.</p>
            <p className="mt-2 text-ink/75">
              Your answers above are preserved. Two options:
            </p>
            <ol className="mt-2 grid gap-1 pl-4 text-ink/75">
              <li>1. Wait a moment and click Send again.</li>
              <li>
                2. Or send the same answers by email — I've drafted them for you below.
              </li>
            </ol>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <a
                href={buildMailtoFallback(form)}
                className="inline-flex items-center justify-center rounded-sm border border-ink/20 bg-white px-4 py-2 text-sm font-semibold text-ink no-underline hover:border-ink"
              >
                Open email with answers pre-filled →
              </a>
              {submitError && submitError.status !== null && (
                <span className="text-xs text-ink/55">
                  (debug: HTTP {submitError.status})
                </span>
              )}
            </div>
            <p className="mt-3 text-xs text-ink/55">
              Note: nothing was saved on my side. If you reach me by email, that becomes the record.
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-ink/10 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <p className="text-xs text-ink/55">
          By submitting, you confirm this message contains no confidential or regulated
          data.
        </p>
        <Button type="submit" size="lg" disabled={submitStatus === 'submitting'} className="w-full sm:w-auto">
          {submitStatus === 'submitting' ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </form>
  );
}
